/**
 * Small display helpers shared by the console + call log pages:
 * direction/engine badges, status cells, and plain-language labels.
 */

import { Badge } from "../Badge";
import { StatusDot, callStatusTone } from "../StatusDot";
import type { CallDirection, CallStatus, EndReason, VoiceEngine } from "../../lib/types";

// ---------------------------------------------------------------------------
// Labels — plain language, per the frontend copy rule
// ---------------------------------------------------------------------------

export function engineLabel(engine: VoiceEngine | string | null | undefined): string {
  if (engine === "cascaded") return "Standard";
  if (engine === "realtime") return "Realtime";
  if (engine === "gemini") return "Natural";
  return "—";
}

export function directionLabel(direction: CallDirection): string {
  switch (direction) {
    case "inbound":
      return "Inbound";
    case "outbound":
      return "Outbound";
    case "browser_test":
      return "Browser test";
  }
}

export function statusLabel(status: CallStatus): string {
  switch (status) {
    case "initiating":
      return "Initiating";
    case "in_progress":
      return "In progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "no_answer":
      return "No answer";
  }
}

export function endReasonLabel(reason: EndReason | null | undefined): string {
  switch (reason) {
    case "caller_hangup":
      return "Caller hung up";
    case "agent_hangup":
      return "Agent ended the call";
    case "error":
      return "Error";
    case "timeout":
      return "Time limit reached";
    default:
      return "—";
  }
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export function DirectionBadge({ direction }: { direction: CallDirection }) {
  return (
    <Badge tone={direction === "browser_test" ? "bright" : "default"}>
      {directionLabel(direction)}
    </Badge>
  );
}

export function EngineBadge({ engine }: { engine: VoiceEngine | string | null | undefined }) {
  if (engine !== "cascaded" && engine !== "realtime" && engine !== "gemini") return null;
  return <Badge mono>{engineLabel(engine)}</Badge>;
}

export function CallStatusCell({ status }: { status: CallStatus }) {
  return (
    <StatusDot
      tone={callStatusTone(status)}
      label={statusLabel(status)}
      pulse={status === "in_progress"}
    />
  );
}
